import {useCallback, useContext} from 'react';

import {ValidationContext} from 'src/contexts/validation-context';
import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {UpdateAlertRequest} from 'src/modules/platform-alert-builder/typedef';
import {isEndTimeBeforeStartTime} from 'src/modules/platform-alert-builder/utils';
import {getAccountName} from 'src/openapi/admin/apis/GetAccountNameApi';
import {getPlatformAlertById, updatePlatformAlert} from 'src/openapi/admin/apis/PlatformAlertsApi';
import {useAppDispatch} from 'src/store/hooks';

import {
    ALERT_ACCOUNT_CONTROL,
    ALERT_ACCOUNT_REQUIRED_ERROR,
    ALERT_ACCOUNT_REQUIRED_MESSAGE,

    ALERT_END_DATE_CONTROL,
    ALERT_END_DATE_REQUIRED_ERROR,
    ALERT_END_DATE_REQUIRED_MESSAGE,

    ALERT_END_TIME_BEFORE_START_TIME_ERROR,
    ALERT_END_TIME_BEFORE_START_TIME_MESSAGE,
    ALERT_END_TIME_CONTROL,
    ALERT_MESSAGE_CONTROL,
    ALERT_MESSAGE_HTML_TOO_LONG_ERROR,
    ALERT_MESSAGE_HTML_TOO_LONG_MESSAGE,
    ALERT_MESSAGE_REQUIRED_ERROR,

    ALERT_MESSAGE_REQUIRED_MESSAGE,
    ALERT_START_BEFORE_END_DATE_ERROR,
    ALERT_START_BEFORE_END_DATE_MESSAGE,
    ALERT_START_DATE_CONTROL,
    ALERT_START_DATE_REQUIRED_ERROR,
    ALERT_START_DATE_REQUIRED_MESSAGE,

    MAX_HTML_LENGTH,
} from '../constants';
import {DEFAULT_REQUEST} from '../typedef';

import type {AccountShortInfo} from 'src/openapi/admin/models/AccountShortInfo';

type Props = {
    alertId: number,
};

export const useEditAlert = ({alertId}: Props) => {
    const apiDispatch = useAppDispatch();
    const {addError, removeError, validationErrors} = useContext(ValidationContext);

    const getDispatchAction = useCallback(async () => {
        const loadedAlert = await apiDispatch(getPlatformAlertById({alertId}));

        if (loadedAlert.payload.success) {
            const accountId = loadedAlert.payload.data.accountId;
            let account: AccountShortInfo | null = null;

            if (loadedAlert.payload.data.accountId) {
                const accountNameResponse = await apiDispatch(getAccountName({
                    requestBody: [accountId],
                }));

                if (accountNameResponse.payload.success) {
                    account = {accountId, accountName: accountNameResponse.payload.data[accountId]};
                }
            }

            const payload: {success: true, error: false, data: UpdateAlertRequest} = {
                success: true,
                error: false,
                data: {
                    forAllUsers: !account,
                    account,
                    alertType: loadedAlert.payload.data.alertType,
                    message: loadedAlert.payload.data.message,
                    startDate: loadedAlert.payload.data.startDate,
                    endDate: loadedAlert.payload.data.endDate,
                    startTime: loadedAlert.payload.data.startTime,
                    endTime: loadedAlert.payload.data.endTime,
                },
            };
            return Promise.resolve({type: '', payload});
        } else {
            return Promise.resolve({type: '', payload: loadedAlert.payload});
        }
    }, [alertId, apiDispatch]);

    const saveDispatchAction = useCallback(
        (updatePlatformAlertRequest: UpdateAlertRequest, signal: AbortSignal, reason?: string) => apiDispatch(
            updatePlatformAlert({
                alertId,
                updatePlatformAlertRequest: {
                    accountId: !updatePlatformAlertRequest.forAllUsers && updatePlatformAlertRequest.account
                        ? updatePlatformAlertRequest.account.accountId : undefined,
                    alertType: updatePlatformAlertRequest.alertType,
                    message: updatePlatformAlertRequest.message,
                    startDate: updatePlatformAlertRequest.startDate,
                    endDate: updatePlatformAlertRequest.endDate,
                    startTime: updatePlatformAlertRequest.startTime,
                    endTime: updatePlatformAlertRequest.endTime,
                },
                reason,
            }, {signal}),
        ),
        [alertId, apiDispatch],
    );

    const editValidation = useCallback((request: UpdateAlertRequest) => {
        if (request.message) {
            removeError(ALERT_MESSAGE_CONTROL);
        }

        if (request.message.length > MAX_HTML_LENGTH) {
            addError({
                controlId: ALERT_MESSAGE_CONTROL,
                code: ALERT_MESSAGE_HTML_TOO_LONG_ERROR,
                message: ALERT_MESSAGE_HTML_TOO_LONG_MESSAGE,
            });
        } else {
            removeError(ALERT_MESSAGE_CONTROL, [ALERT_MESSAGE_HTML_TOO_LONG_ERROR]);
        }

        if (request.account || request.forAllUsers) {
            removeError(ALERT_ACCOUNT_CONTROL);
        }

        if (request.startDate) {
            removeError(ALERT_START_DATE_CONTROL, [ALERT_START_DATE_REQUIRED_ERROR]);
        }

        if (request.endDate) {
            removeError(ALERT_END_DATE_CONTROL, [ALERT_END_DATE_REQUIRED_ERROR]);
        }

        if (new Date(request.startDate) > new Date(request.endDate)) {
            addError({
                controlId: ALERT_END_DATE_CONTROL,
                code: ALERT_START_BEFORE_END_DATE_ERROR,
                message: ALERT_START_BEFORE_END_DATE_MESSAGE,
            });
        } else {
            removeError(ALERT_END_DATE_CONTROL, [ALERT_START_BEFORE_END_DATE_ERROR]);
        }

        if (isEndTimeBeforeStartTime(request)) {
            addError({
                controlId: ALERT_END_TIME_CONTROL,
                code: ALERT_END_TIME_BEFORE_START_TIME_ERROR,
                message: ALERT_END_TIME_BEFORE_START_TIME_MESSAGE,
            });
        } else {
            removeError(ALERT_END_TIME_CONTROL, [ALERT_END_TIME_BEFORE_START_TIME_ERROR]);
        }

    }, [addError, removeError]);

    const saveValidation = useCallback((request: UpdateAlertRequest) => {
        let valid = true;

        if (!request.message) {
            addError({
                controlId: ALERT_MESSAGE_CONTROL,
                code: ALERT_MESSAGE_REQUIRED_ERROR,
                message: ALERT_MESSAGE_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        if (!request.forAllUsers && !request.account) {
            addError({
                controlId: ALERT_ACCOUNT_CONTROL,
                code: ALERT_ACCOUNT_REQUIRED_ERROR,
                message: ALERT_ACCOUNT_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        if (!request.startDate) {
            addError({
                controlId: ALERT_START_DATE_CONTROL,
                code: ALERT_START_DATE_REQUIRED_ERROR,
                message: ALERT_START_DATE_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        if (!request.endDate) {
            addError({
                controlId: ALERT_END_DATE_CONTROL,
                code: ALERT_END_DATE_REQUIRED_ERROR,
                message: ALERT_END_DATE_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        if (isEndTimeBeforeStartTime(request)) {
            addError({
                controlId: ALERT_END_TIME_CONTROL,
                code: ALERT_END_TIME_BEFORE_START_TIME_ERROR,
                message: ALERT_END_TIME_BEFORE_START_TIME_MESSAGE,
            });

            valid = false;
        }

        if (validationErrors.some(({code}) => code !== ALERT_END_TIME_BEFORE_START_TIME_ERROR)) {
            valid = false;
        }

        return valid;
    }, [validationErrors, addError]);

    return useCommonDataHook({
        initValue: DEFAULT_REQUEST,
        getDispatchAction,
        saveDispatchAction,
        editValidation,
        saveValidation,
    });
};
